// Copyright (c) 2026 Celadon Development LLC, All rights reserved.
// Author Vasily Klyuev <vasily.klyuev@celadon.ae>
import {
  call, put, select, takeEvery
} from 'redux-saga/effects';

import * as types from './actionTypes';
import * as redeemActions from './actions';
import { scanVoucherService, redeemVoucherService } from './service';
import { AMOUNT_PATTERN, cleanAmount } from '../../../services/voucherAmount';
import * as redeemSelectors from './selectors';

const credentials = () => ({
  CustomerId: localStorage.getItem('loggedID'),
  Token: localStorage.getItem('token'),
});

/**
 * Reads whatever the endpoint gave us as a reason. The voucher endpoints
 * return typed refusals - expired, already spent, issued by another shop -
 * and those messages are written for the operator, so they are shown as-is.
 */
const reasonFrom = (response, fallback) =>
  (response.body && (response.body.Reason || response.body.Message)) || fallback;

function* scanVoucherSaga() {
  // Brackets matter: || binds tighter than yield, so "yield select(x) || ''"
  // falls back on the effect object rather than the value and the guard never
  // fires - .trim() then throws the moment the store holds undefined.
  const code = ((yield select(redeemSelectors.getCode)) || '').trim();

  if (!code) {
    yield put(redeemActions.setMessage({ error: true, text: 'Enter or scan a voucher code.' }));
    return;
  }

  yield put(redeemActions.startLoader());

  const response = yield call(scanVoucherService, { ...credentials(), Code: code });

  if (response.ok) {
    // A code matching nothing still comes back 200, as a placeholder with the
    // code echoed back and every other field left at its default. Shown as a
    // voucher that reads as a real one worth nothing - £0.00 balance, blank
    // status, no expiry - which is a different thing to say to a customer than
    // "that code is not a voucher". An identity tells the two apart: a voucher
    // that exists always has one.
    if (!(response.body && response.body.VoucherId)) {
      // Clears any voucher left from an earlier lookup, so a failed scan can
      // never leave the previous customer's balance on screen.
      yield put(redeemActions.scanVoucherSuccess(null));
      yield put(redeemActions.setMessage({
        error: true,
        text: reasonFrom(response, 'Voucher not found. Check the code and try again.'),
      }));
      yield put(redeemActions.stopLoader());
      return;
    }

    yield put(redeemActions.scanVoucherSuccess(response.body));

    // A voucher that does exist but cannot be used - expired, cancelled or
    // spent - still shows its panel: the balance and expiry date on it are
    // what the operator reads out when explaining why. Only the reason is
    // added underneath.
    if (!response.body.IsRedeemable) {
      yield put(redeemActions.setMessage({
        error: true,
        text: response.body.Reason || 'This voucher cannot be redeemed.',
      }));
    }
  } else {
    yield put(redeemActions.setMessage({
      error: true,
      text: reasonFrom(response, 'Voucher not found. Check the code and try again.'),
    }));
  }

  yield put(redeemActions.stopLoader());
}

function* redeemVoucherSaga() {
  const voucher = yield select(redeemSelectors.getVoucher);
  const amount = cleanAmount(yield select(redeemSelectors.getAmount));

  if (!voucher) {
    yield put(redeemActions.setMessage({ error: true, text: 'Look the voucher up first.' }));
    return;
  }

  if (!AMOUNT_PATTERN.test(amount) || parseFloat(amount) <= 0) {
    yield put(redeemActions.setMessage({ error: true, text: 'Enter an amount, for example 2.76.' }));
    return;
  }

  // Checked here for a quick answer, and again by the server, which is the
  // authority - the balance on screen could be a few seconds stale.
  if (parseFloat(amount) > parseFloat(voucher.Balance)) {
    yield put(redeemActions.setMessage({
      error: true,
      text: `That is more than the £${parseFloat(voucher.Balance).toFixed(2)} left on this voucher.`,
    }));
    return;
  }

  yield put(redeemActions.startLoader());

  const response = yield call(redeemVoucherService, {
    ...credentials(),
    Code: voucher.Code,
    Amount: amount,
  });

  if (response.ok) {
    yield put(redeemActions.redeemVoucherSuccess(response.body));
    yield put(redeemActions.setMessage({
      error: false,
      text: `£${parseFloat(response.body.AmountUsed).toFixed(2)} redeemed. `
        + `£${parseFloat(response.body.BalanceRemaining).toFixed(2)} left on the voucher.`,
    }));
  } else {
    yield put(redeemActions.setMessage({
      error: true,
      text: reasonFrom(response, 'Could not redeem the voucher. Please try again.'),
    }));
  }

  yield put(redeemActions.stopLoader());
}

const redeemVoucherSagas = [
  takeEvery(types.REDEEM_SCAN_VOUCHER, scanVoucherSaga),
  takeEvery(types.REDEEM_VOUCHER, redeemVoucherSaga),
];

export default redeemVoucherSagas;
